import { defineStore } from 'pinia'
import { fetchMe, type KeiSession } from '@/services/auth'

type SessionStatus = 'idle' | 'loading' | 'authenticated' | 'guest'

export const useAuthStore = defineStore('meetAuth', {
  state: () => ({
    status: 'idle' as SessionStatus,
    session: null as KeiSession | null,
    // 2026-09-14 real bug: a guest who joined a room earlier set
    // `status: 'authenticated'` (see setGuestIdentity below) so the rest of
    // the app treats them as "has a session" — but MeetHome then also read
    // `isAuthenticated` to decide whether to show the room-CREATE buttons,
    // and room creation is member-only (server/routes/meet.js). Result:
    // "로그인이 되었다고 생각해서" a guest still sees 새 미팅 만들기/바로
    // 만나기, clicks it, gets 401, sees a confusing error — because "has any
    // session" and "is a real member who can create rooms" got conflated
    // into one flag. This tracks them separately.
    isGuestSession: false as boolean
  }),
  getters: {
    isAuthenticated: (state) => state.status === 'authenticated' && state.session !== null,
    canCreateMeetings: (state) => state.status === 'authenticated' && state.session !== null && !state.isGuestSession,
    displayName: (state) => state.session?.displayName ?? state.session?.email?.split('@')[0] ?? null
  },
  actions: {
    async fetchSession() {
      this.status = 'loading'
      const session = await fetchMe()
      if (session) {
        this.session = session
        this.isGuestSession = false
        this.status = 'authenticated'
      } else {
        this.session = null
        this.status = 'guest'
      }
    },
    /**
     * 2026-09-14 — a guest who joins a MEET room gets a real backend session
     * (server/routes/meet.js's createMeetGuestSession issues an actual
     * kei_token cookie for a temporary GUEST-role User), so from this point
     * on they behave like any authenticated session for the rest of the app
     * (ProfileSheet, MeetRoom's local participant, useRealtimeRoom, etc.) —
     * this just reflects that server-side fact into the client store instead
     * of leaving it stuck on `status: 'guest'` with no session.
     */
    setGuestIdentity(userId: string, displayName: string) {
      this.session = {
        userId,
        email: null,
        displayName,
        avatarUrl: null,
        profileHandle: null,
        languages: [],
        organization: null
      }
      this.isGuestSession = true
      this.status = 'authenticated'
    }
  }
})
